import { useRef } from 'react'
import { To, useNavigate } from 'react-router'

import Lightbox, {
	ClickCallbackProps,
	ControllerRef
} from 'yet-another-react-lightbox'
import Inline from 'yet-another-react-lightbox/plugins/inline'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'

import { Image } from '@wwt/entities/api/search-response'
import LoadingIcon from '@wwt/shared/assets/icons/outline/loading.svg'

import { cn } from '@/shared/lib/cn.ts'
import { NavigationButton } from '@/shared/ui/NavigationButton.tsx'

import { getSlides } from '../util/getSlides'

interface ImageSliderProps {
	images: Image[]
	index?: number
	onImageClick?: (index: number) => void
	to?: To
	title?: string
	classNames?: {
		base?: string
		slide?: string
	}
}

/**
 * Image slider component
 *
 * Note:
 * Without Zoom plugin, lightbox chooses image from srcSet based on its width only.
 * With Zoom plugin, lightbox chooses image from srcSet based on both width and height.
 * With Zoom plugin, lightbox chooses images that better fit for room-offer card.
 */
export const ImageSlider = ({
	images,
	index = 0,
	onImageClick,
	to,
	title,
	classNames
}: ImageSliderProps) => {
	const ref = useRef<ControllerRef>(null)

	const slides = getSlides(images)
	const navigate = useNavigate()

	const handleClick = (props: ClickCallbackProps) => {
		onImageClick?.(props.index)

		if (to) {
			navigate(to)
		}
	}

	return (
		<div className={cn('relative z-1', classNames?.base)}>
			<Lightbox
				index={index}
				on={{
					click: handleClick
				}}
				plugins={[Inline, Zoom]}
				slides={slides}
				controller={{ ref }}
				carousel={{
					spacing: 0,
					padding: 0,
					imageFit: 'cover'
				}}
				styles={{
					container: {
						backgroundColor: 'var(--background)'
					},
					toolbar: {
						display: 'none'
					}
				}}
				render={{
					slideContainer: ({ children }) => (
						<div
							className={cn(
								'h-full w-full cursor-pointer overflow-hidden bg-gray-100 md:rounded-md',
								classNames?.slide
							)}
							title={title}
						>
							{children}
						</div>
					),
					buttonPrev: () => {
						return images.length > 1 ? (
							<NavigationButton
								classNames={{
									base: 'bg-white h-9 w-9 rounded-full absolute left-2 z-1',
									icon: 'size-4!'
								}}
								data-wwt-id="image-slider--navigation-back--button"
								size="icon-sm"
								direction="left"
								aria-label="slider navigation to the left"
								onClick={() => ref.current?.prev()}
							/>
						) : null
					},
					buttonNext: () => {
						return images.length > 1 ? (
							<NavigationButton
								classNames={{
									base: 'bg-white h-9 w-9 rounded-full absolute right-2 z-1',
									icon: 'size-4!'
								}}
								data-wwt-id="image-slider--navigation-back--button"
								size="icon-sm"
								direction="right"
								aria-label="slider navigation to the right"
								onClick={() => ref.current?.next()}
							/>
						) : null
					},
					iconError: () => (
						<LoadingIcon className="text-primary animate-spin" />
					),
					iconLoading: () => (
						<LoadingIcon className="text-primary animate-spin" />
					)
				}}
			/>
		</div>
	)
}
